// src/pages/InternalThread.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { internalApi } from "../api/internal";
import { useAuth } from "../auth/AuthContext";
import { useRealtime } from "../realtime/RealtimeProvider";
import ParticipantsModal from "../components/internal/ParticipantsModal";
import TasksPanel from "../components/internal/TasksPanel";
import LinkLeadContactModal from "../components/internal/LinkLeadContactModal";

export default function InternalThread() {
  const { id } = useParams();
  const threadId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const rt = useRealtime();

  const [thread, setThread] = useState(null);
  const [perm, setPerm] = useState({ can_post: 0, can_manage: 0, can_reply_customer: 0, role: "viewer" });

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const msgRef = useRef(null);
  const stickRef = useRef(true);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const STICK_THRESHOLD = 120;

  async function loadThread() {
    setLoading(true);
    try {
      const { data } = await internalApi.getThread(threadId);
      setThread(data?.thread || null);
      setPerm({
        role: data?.permissions?.role || "viewer",
        can_post: data?.permissions?.can_post ? 1 : 0,
        can_manage: data?.permissions?.can_manage ? 1 : 0,
        can_reply_customer: data?.permissions?.can_reply_customer ? 1 : 0,
      });
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo cargar");
      navigate("/internal");
    } finally {
      setLoading(false);
    }
  }

  async function loadMessages() {
    try {
      const { data } = await internalApi.listMessages(threadId);
      const rows = data?.rows || [];
      setMessages(rows);
      stickRef.current = true;
      setShowScrollBottom(false);
      setTimeout(() => {
        const el = msgRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }, 0);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo cargar mensajes");
    }
  }

  useEffect(() => {
    if (!threadId) return;
    loadThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    if (!threadId) return;
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    if (!threadId || !rt) return undefined;

    rt.subscribeInternalThread?.(threadId);

    const off = rt.onEvent?.((evt) => {
      const payload = evt.payload || {};

      if (evt.type === "internal.message.new") {
        const incomingThreadId = Number(payload.threadId || payload.thread_id || 0);
        if (incomingThreadId !== threadId) return;

        const message = payload.message || null;
        if (!message?.id) return;

        setMessages((prev) => {
          if (prev.some((m) => String(m.id) === String(message.id))) return prev;
          return [...prev.filter((m) => !String(m.id).startsWith("tmp_")), message];
        });
        stickRef.current = true;
      }

      if (evt.type === "internal.notify") {
        const incomingThreadId = Number(payload.threadId || payload.thread?.id || 0);
        if (incomingThreadId !== threadId) return;
        loadThread();
      }
    });

    return () => {
      off?.();
      rt.unsubscribeInternalThreads?.([threadId]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, rt]);

  useEffect(() => {
    const el = msgRef.current;
    if (!el) return;
    if (stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const title = useMemo(() => {
    if (!thread) return "";
    return (
      thread.title ||
      (thread.lead_id ? `Lead #${thread.lead_id}` : thread.contact_id ? `Contacto #${thread.contact_id}` : "Conversación general")
    );
  }, [thread]);

  const tag = useMemo(() => {
    if (!thread) return "";
    if (thread.lead_id) return `LEAD #${thread.lead_id}`;
    if (thread.contact_id) return `CONTACTO #${thread.contact_id}`;
    return "GENERAL";
  }, [thread]);

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return String(iso).replace("T", " ").slice(0, 19);
    }
  };

  const sameDay = (a, b) => {
    const da = new Date(a), db = new Date(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
  };
  const dayLabel = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const y = new Date();
    y.setDate(today.getDate() - 1);
    if (sameDay(d, today)) return "Hoy";
    if (sameDay(d, y)) return "Ayer";
    return d.toLocaleDateString();
  };

  const onMessagesScroll = () => {
    const el = msgRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const sticky = dist < STICK_THRESHOLD;
    stickRef.current = sticky;
    setShowScrollBottom(!sticky);
  };

  const send = async () => {
    if (!perm.can_post) return;
    const t = text.trim();
    if (!t) return;

    setSending(true);
    try {
      // optimistic
      const tmp = {
        id: `tmp_${Date.now()}`,
        thread_id: threadId,
        user_id: user?.id || -1,
        user_name: "Tú",
        kind: "text",
        text: t,
        meta_json: null,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tmp]);
      stickRef.current = true;

      const { data } = await internalApi.postMessage(threadId, { text: t, kind: "text" });
      const m = data?.message;
      if (m) {
        // reemplaza tmp por el real (si quieres)
        setMessages((prev) => {
          const next = [...prev];
          const i = next.findIndex((x) => String(x.id).startsWith("tmp_") && x.text === t);
          if (i >= 0) next[i] = m;
          else next.push(m);
          return next;
        });
      }
      setText("");
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo enviar");
      loadMessages();
    } finally {
      setSending(false);
    }
  };

  const closeThread = async () => {
    if (!perm.can_manage) return;
    try {
      const { data } = await internalApi.patchThread(threadId, { status: "closed" });
      setThread(data?.thread || thread);
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo cerrar");
    }
  };

  if (loading && !thread) {
    return (
      <div className="chat-root">
        <style>{css}</style>
        <div className="card center-pane" style={{ margin: 10 }}>
          <div className="section">
            <div className="muted">Cargando…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-root">
      <style>{css}</style>

      {/* Topbar igual a Chat.jsx */}
      <div className="topbar">
        <div className="left-actions">
          <button className="btn back" onClick={() => navigate("/internal")} title="Volver a Chat Interno">
            ← Interno
          </button>
        </div>

        <div className="right-info" style={{ marginLeft: "auto" }}>
          {thread ? (
            <div className="unread">
              {tag} · <b>{String(thread.status || "open").toUpperCase()}</b>
            </div>
          ) : null}
        </div>
      </div>

      <div className="chat-grid left-collapsed">
        <div className="card center-pane">
          {/* Header (misma clase/estética) */}
          <div className="section header-row">
            <div className="header-left">
              <div className="title">{thread ? title : "Conversación"}</div>
              {thread ? (
                <div className="muted small">
                  Permisos: {perm.role} · post={perm.can_post ? "sí" : "no"} · manage={perm.can_manage ? "sí" : "no"}
                </div>
              ) : null}
            </div>

            <div className="header-actions">
              <button className="icon-btn" onClick={() => setParticipantsOpen(true)} title="Participantes" aria-label="Participantes">
                <svg viewBox="0 0 24 24">
                  <path
                    d="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0Zm-12 10c0-3.3 3.1-6 8-6s8 2.7 8 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <button
                className={`icon-btn ${perm.can_manage ? "" : "disabled"}`}
                onClick={() => setLinkOpen(true)}
                disabled={!perm.can_manage}
                title="Vincular a Lead/Contacto"
                aria-label="Vincular"
              >
                <svg viewBox="0 0 24 24">
                  <path
                    d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <path
                    d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <button
                className={`icon-btn ${perm.can_manage ? "" : "disabled"}`}
                onClick={closeThread}
                disabled={!perm.can_manage || String(thread?.status || "").toLowerCase() === "closed"}
                title="Cerrar conversación"
                aria-label="Cerrar"
              >
                <svg viewBox="0 0 24 24">
                  <path
                    d="M4 7h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7zm3-3h10v3H7V4z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Mensajes (mismo look bubbles) */}
          <div ref={msgRef} onScroll={onMessagesScroll} className="messages">
            {!messages.length ? (
              <div className="muted">Aún no hay mensajes.</div>
            ) : (
              (() => {
                let lastDate = "";
                const blocks = [];
                for (const m of messages) {
                  const created = m.created_at || new Date().toISOString();
                  const dateStr = String(created).slice(0, 10);
                  if (dateStr && dateStr !== lastDate) {
                    blocks.push(
                      <div className="date-sep" key={`sep_${dateStr}`}>
                        {dayLabel(created)}
                      </div>
                    );
                    lastDate = dateStr;
                  }

                  const bySystem = !m.user_id;
                  const isOut =
                    !bySystem &&
                    (Number(m.user_id) === Number(user?.id) ||
                      String(m.user_name || "").toLowerCase() === "tú");
                  const who = bySystem ? "Sistema" : m.user_name || `User#${m.user_id}`;

                  blocks.push(
                    <div key={m.id} className={`msg-row ${bySystem ? "left" : isOut ? "right" : "left"}`}>
                      <div className={`bubble ${bySystem ? "in" : isOut ? "out" : "in"}`}>
                        <div className="bubble-body">
                          {m.text ? (
                            <div className="bubble-text">{m.text}</div>
                          ) : m.kind === "attachment" ? (
                            <div className="bubble-text">[Adjunto]</div>
                          ) : null}
                        </div>
                        <div className="msg-meta">
                          {who} · {fmtTime(created)}
                        </div>
                      </div>
                    </div>
                  );
                }
                return blocks;
              })()
            )}

            {showScrollBottom && (
              <button
                className="scroll-bottom"
                onClick={() => {
                  const el = msgRef.current;
                  if (el) el.scrollTop = el.scrollHeight;
                  stickRef.current = true;
                  setShowScrollBottom(false);
                }}
              >
                ↓
              </button>
            )}
          </div>

          {/* Composer (misma estética input-bar) */}
          <div className="section input-bar">
            <textarea
              className="input"
              placeholder={perm.can_post ? "Escribe un mensaje interno…" : "No tienes permiso para escribir"}
              value={text}
              disabled={!perm.can_post || sending || String(thread?.status || "").toLowerCase() === "closed"}
              onChange={(e) => setText(e.target.value)}
              onInput={(e) => {
                e.currentTarget.style.height = "40px";
                e.currentTarget.style.height = Math.min(e.currentTarget.scrollHeight, 160) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              style={{ resize: "none", minHeight: 40, maxHeight: 160 }}
            />

            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" onClick={send} disabled={!perm.can_post || sending || !text.trim()}>
                {sending ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </div>
        </div>

        {/* Panel derecha: Tasks (mismo look card) */}
        <div className="card" style={{ minWidth: 320 }}>
          <div className="section" style={{ borderBottom: "1px solid var(--border)" }}>
            <div style={{ fontWeight: 800 }}>Tareas</div>
            <div className="muted small">Acciones internas por conversación</div>
          </div>
          <div style={{ padding: 0 }}>
            <TasksPanel threadId={threadId} canManage={!!perm.can_manage} />
          </div>
        </div>
      </div>

      <ParticipantsModal
        open={participantsOpen}
        onClose={() => setParticipantsOpen(false)}
        threadId={threadId}
        canManage={!!perm.can_manage}
      />

      <LinkLeadContactModal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        thread={thread}
        canManage={!!perm.can_manage}
        onSaved={(updated) => setThread(updated)}
      />
    </div>
  );
}

/* CSS: reutiliza el mismo “tema” de Chat.jsx (mismo root vars + clases) */
const css = `
:root{
  --border: rgba(255,255,255,.08);
  --bg: #0b1220;
  --panel: #0f172a;
  --text: #e5e7eb;
  --muted: #94a3b8;
  --brand: #3b82f6;
  --brand-2: #2563eb;
}

.chat-root{ height: calc(100vh - 80px); background: var(--bg); color: var(--text); display:grid; grid-template-rows:auto 1fr; }
.topbar{
  display:flex; align-items:center; gap:10px; padding:10px 12px; border-bottom:1px solid var(--border); background: rgba(255,255,255,.02);
}
.left-actions{ display:flex; align-items:center; gap:8px; }
.btn.back{ background: rgba(255,255,255,.06); border:1px solid var(--border); color:#eaf2ff; padding:6px 12px; border-radius:10px; cursor:pointer; }
.btn.back:hover{ background: rgba(59,130,246,.15); border-color: rgba(59,130,246,.35); }

.right-info{ display:flex; align-items:center; gap:10px; margin-left:auto; }
.unread{ color:#cfe1ff; background: rgba(59,130,246,.15); border:1px solid rgba(59,130,246,.35); padding:4px 10px; border-radius:999px; font-size:12px; }
.unread b{ color:#fff; }

.chat-grid{
  display:grid; grid-template-columns: 1fr 360px; gap:12px; height:100%; overflow:hidden; padding:10px;
}
@media (max-width: 1200px) { .chat-grid { grid-template-columns: 1fr; } }

.card { background: var(--panel); border-radius: 14px; border: 1px solid var(--border); overflow: hidden; }
.section { padding: 12px; }

.header-row { border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 12px; max-height: 52px; }
.header-left .title { font-weight: 700; font-size: 15px; color: #fff; }
.header-actions { display: flex; gap: 8px; align-items: center; }

.icon-btn { width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--border); background: rgba(255,255,255,.04); display: grid; place-items: center; color: var(--muted); cursor: pointer; }
.icon-btn:hover { color: #dbe5ff; background: rgba(59,130,246,.15); border-color: rgba(59,130,246,.35); }
.icon-btn.disabled{ opacity:.45; cursor:not-allowed; }
.icon-btn svg { width: 18px; height: 18px; }

.messages { padding: 12px; overflow-y: auto; background: #0b1220; position: relative; }
.date-sep { position: sticky; top: 6px; width: max-content; margin: 10px auto; padding: 2px 10px; border-radius: 999px; background: #1f2937; color: #cbd5e1; font-size: 12px; border: 1px solid #273446; }

.msg-row { display: flex; margin: 8px 0; }
.msg-row.right { justify-content: flex-end; }
.msg-row.left { justify-content: flex-start; }

.bubble { max-width: 72%; padding: 10px 12px; border-radius: 14px; border: 1px solid #1e293b; background: #111827; color: #e5e7eb; }
.bubble.out { background: #0b2a4a; border-color: #1f3a5d; }
.bubble-text { white-space: pre-wrap; color: #e5e7eb; }
.msg-meta { font-size: 11px; color: #9ca3af; margin-top: 4px; }

.scroll-bottom { position: absolute; right: 14px; bottom: 14px; padding: 6px 10px; border-radius: 999px; border: 1px solid #273446; background: #0f172a; color: #cbd5e1; box-shadow: 0 6px 20px rgba(0,0,0,.25); }

.input{ width:100%; background: rgba(255,255,255,.04); border: 1px solid var(--border); color: var(--text); padding: 8px 12px; border-radius: 10px; }
.input:focus { outline: none; border-color: rgba(59,130,246,.45); box-shadow: 0 0 0 3px rgba(59,130,246,.15); }

.btn { background: rgba(255,255,255,.05); border: 1px solid var(--border); color: var(--text); border-radius: 10px; padding: 8px 12px; cursor: pointer; }
.btn:hover { background: rgba(255,255,255,.08); }
.btn-primary { background: var(--brand); border-color: var(--brand-2); font-weight:800; }
.btn-primary:hover { background: var(--brand-2); }

.input-bar { display: grid; grid-template-columns: 1fr auto; gap: 8px; border-top: 1px solid var(--border); align-items:end; }
.muted { color: var(--muted); }
.small { font-size: 12px; }
`;
